import { price as priceUtil } from '@eti/utils';
import { css, mediaQueries } from '@eti/styles';
import { useSiteContext, useTranslation } from '@eti/providers';
import { Card, Inline, Stack, Text, textVariants } from '@etg/wings';
import { Fragment } from 'react';
import { ScreenReaderPrice } from '@eti/components';
import type { TravelerDisplayPricesType } from '../utils';
import { getAgeTypeLabel, getAgeTypeLabelForQuantity } from '../../../../../../utils/travelers';

const itemStyles = css`
  justify-content: space-between;
  padding-inline-start: 8px;
`;

const taxesAndFeesCategoryLabelStyles = css`
  padding-inline-start: 8px;
`;

const taxesAndFeesPriceStyles = css`
  white-space: nowrap;
`;

const vatLabelStyles = css`
  flex: 0 0 75%;

  @media ${mediaQueries.large.up} {
    flex: 0 0 70%;
  }
`;

const vatPriceStyles = css`
  text-align: right;

  html[dir='rtl'] & {
    text-align: left;
  }
`;

interface TravelersProps {
  marketingCarrierNames?: string;
  shouldShowTotalMarkup: boolean;
  travelersPrices: TravelerDisplayPricesType[];
}

const Travelers = ({
  marketingCarrierNames,
  shouldShowTotalMarkup,
  travelersPrices,
}: TravelersProps) => {
  const { market } = useSiteContext();
  const { t } = useTranslation();

  const getPriceLabel = (ageTypeSingularLabel: string) => {
    if (shouldShowTotalMarkup && marketingCarrierNames) {
      return t('Cart.PricePerAgeType.WithCarrierName', ageTypeSingularLabel, marketingCarrierNames);
    }
    return t('Cart.PricePerAgeType', ageTypeSingularLabel);
  };

  const getVatLabel = (ageTypeSingularLabel: string) => {
    if (marketingCarrierNames) {
      if (shouldShowTotalMarkup) {
        return t('Cart.TaxesAndAirlineFees.WithCarriers', marketingCarrierNames);
      }
      return t(
        'Cart.TaxesAndChargesPerAgeType.WithCarriers',
        ageTypeSingularLabel,
        marketingCarrierNames,
      );
    }
    return t('Cart.TaxesAndChargesPerAgeType', ageTypeSingularLabel);
  };

  return (
    <Card.Section>
      <Stack>
        {travelersPrices.map(
          ({ ageType, markup, price, quantity, taxesAndFees, totalPrice, totalPriceSum, vat }) => {
            const shouldShowTaxBreakdown = taxesAndFees?.length > 0 && vat;
            const ageTypeSingularLabel = t(getAgeTypeLabel(ageType)).toLowerCase();

            return (
              <Stack
                data-testid={`priceSpecification-traveler-${ageType}-price-info`}
                key={`priceSpecification-traveler-${ageType}-price-info`}
                spacing={4}
              >
                <Text
                  data-testid={`priceSpecification-traveler-${ageType}-ageType-label`}
                  variant={textVariants._14_BOLD}
                >
                  {t(getAgeTypeLabelForQuantity(ageType, quantity), quantity)}
                </Text>
                <Stack spacing={4}>
                  {shouldShowTaxBreakdown && (
                    <Stack spacing={4}>
                      {taxesAndFees.map(
                        ({ category, taxes }) =>
                          taxes.length > 0 && (
                            <Fragment key={category}>
                              {market.code !== 'US' && (
                                <Text
                                  className={taxesAndFeesCategoryLabelStyles}
                                  data-testid={`priceSpecification-traveler-${ageType}-taxOrFee-${category}`}
                                  variant={textVariants._14_BOLD}
                                >
                                  {t(`Cart.TaxBreakdown.Category.${category}`)}
                                </Text>
                              )}
                              {taxes.map(
                                (tax) =>
                                  tax &&
                                  !(shouldShowTotalMarkup && tax.code === 'FEE') &&
                                  tax.amount.value !== 0 && (
                                    <Inline className={itemStyles} key={`${category}-${tax.title}`}>
                                      <Text
                                        data-testid={`priceSpecification-traveler-${ageType}-taxOrFee-${category}-${tax.title}`}
                                      >
                                        {tax.title}
                                      </Text>
                                      <Text
                                        aria-hidden
                                        className={taxesAndFeesPriceStyles}
                                        data-testid={`priceSpecification-traveler-${ageType}-taxOrFee-${category}-${tax.title}-price`}
                                      >
                                        {priceUtil.formatPrice(tax.amount.value)}
                                      </Text>
                                      <ScreenReaderPrice
                                        price={priceUtil.formatPrice(tax.amount.value)}
                                      />
                                    </Inline>
                                  ),
                              )}
                            </Fragment>
                          ),
                      )}
                    </Stack>
                  )}
                  {!shouldShowTaxBreakdown && (
                    <Inline className={itemStyles}>
                      <Text data-testid={`priceSpecification-traveler-${ageType}-price-label`}>
                        {getPriceLabel(ageTypeSingularLabel)}
                      </Text>
                      <Text
                        aria-hidden
                        data-testid={`priceSpecification-traveler-${ageType}-price`}
                      >
                        {priceUtil.formatPrice(price)}
                      </Text>
                      <ScreenReaderPrice price={priceUtil.formatPrice(price)} />
                    </Inline>
                  )}
                  {!shouldShowTotalMarkup && !shouldShowTaxBreakdown && markup !== null && (
                    <Inline className={itemStyles}>
                      <Text data-testid={`priceSpecification-traveler-${ageType}-markup-label`}>
                        {markup >= 0
                          ? t('Cart.CostPerAgeType', ageTypeSingularLabel)
                          : t('Cart.DiscountPerAgeType', ageTypeSingularLabel)}
                      </Text>
                      <Text
                        aria-hidden
                        data-testid={`priceSpecification-traveler-${ageType}-markup`}
                      >
                        {priceUtil.formatPrice(markup)}
                      </Text>
                      <ScreenReaderPrice price={priceUtil.formatPrice(markup)} />
                    </Inline>
                  )}
                  {!shouldShowTaxBreakdown && (
                    <Inline className={itemStyles}>
                      <Text
                        className={vatLabelStyles}
                        data-testid={`priceSpecification-traveler-${ageType}-vat-label`}
                      >
                        {getVatLabel(ageTypeSingularLabel)}
                      </Text>
                      <Text
                        aria-hidden
                        className={vatPriceStyles}
                        data-testid={`priceSpecification-traveler-${ageType}-vat`}
                      >
                        {priceUtil.formatPrice(vat)}
                      </Text>
                      <ScreenReaderPrice price={priceUtil.formatPrice(vat)} />
                    </Inline>
                  )}
                  <Inline className={itemStyles}>
                    <Text data-testid={`priceSpecification-traveler-${ageType}-totalPrice-label`}>
                      {t('Cart.TotalPerAgeType', ageTypeSingularLabel)}
                    </Text>
                    <Text
                      aria-hidden
                      data-testid={`priceSpecification-traveler-${ageType}-totalPrice`}
                    >
                      {priceUtil.formatPrice(totalPrice)}
                    </Text>
                    <ScreenReaderPrice price={priceUtil.formatPrice(totalPrice)} />
                  </Inline>
                  {!shouldShowTaxBreakdown && (
                    <Inline className={itemStyles}>
                      <Text
                        data-testid={`priceSpecification-traveler-${ageType}-totalPriceSum-label`}
                        variant={textVariants._14_BOLD}
                      >
                        {t('Cart.Total')}
                      </Text>
                      <Text
                        aria-hidden
                        data-testid={`priceSpecification-traveler-${ageType}-totalPriceSum`}
                        variant={textVariants._14_BOLD}
                      >
                        {priceUtil.formatPrice(totalPriceSum)}
                      </Text>
                      <ScreenReaderPrice price={priceUtil.formatPrice(totalPriceSum)} />
                    </Inline>
                  )}
                </Stack>
              </Stack>
            );
          },
        )}
      </Stack>
    </Card.Section>
  );
};

export default Travelers;
